import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/shared/api/client';
import { Card, CardContent } from '@/shared/ui/card';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs';
import { Label } from '@/shared/ui/label';
import { Input } from '@/shared/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import useRegions from '@/shared/hooks/useRegions';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/shared/ui/dialog';
import { useAuth } from '@/shared/auth/AuthContext';
import useCapacites from '@/shared/auth/useCapacites';
import { CAP } from '@/shared/auth/capacites';
import { toast } from 'sonner';
import { annoncerVerdict } from '@/shared/utils/verdictEnrolement';
import useAjoutActif from '../hooks/useAjoutActif';
import Breadcrumb from '@/shared/components/Breadcrumb';
import { ICON as TYPE_ICON, LABEL as TYPE_LABEL, COLOR as TYPE_COLOR, LIBELLE_IDENTIFIANT } from '@/shared/constants/assetTypes';
import useTypesActifs from '@/shared/hooks/useTypesActifs';
import MentionEstimation from '@/shared/components/MentionEstimation';
import EtatSignatureMandat from '@/shared/components/EtatSignatureMandat';
import {
  dernierPointPvTexte, etatOnduleurTexte, mesureTexte, productionCouranteTexte,
} from '@/shared/utils/liveness';
import {
  CLIENT_STATUS_BANNER_DETAIL as STATUS_BANNER,
  CLIENT_STATUS_CHIP as STATUS_CFG,
  MANDAT_STATUS_CHIP as MANDAT_CFG,
} from '@/shared/constants/statuses';
import { ArrowLeft, FileText, Plus, Download, Pencil, Trash2, Clock, Sun, Snowflake, ThermometerSun, Zap, Activity, ChevronDown, ChevronUp, Save, BatteryCharging, Wifi, WifiOff, HelpCircle, Flame, Droplets, CircleDot, Send } from 'lucide-react';
import OptionsAgregateurs from '@/shared/components/OptionsAgregateurs';
import RechercheAdresse from '@/shared/components/RechercheAdresse';
import { lireCodePostal, ressembleAUnCodePostal } from '@/shared/api/geo';
import RaccordementAgregateurs from '@/features/bridge/components/RaccordementAgregateurs';
import EnvoisAgregateurs from '@/features/bridge/components/EnvoisAgregateurs';
import DeclarationEnedis from '@/features/clients/components/DeclarationEnedis';

const CONN_ICON = { connecte: Wifi, deconnecte: WifiOff, inconnu: HelpCircle };
const CONN_COLOR = { connecte: 'text-emerald-500', deconnecte: 'text-red-500', inconnu: 'text-neutral-400' };
const MODE_ICON = { chauffage: Flame, ECS: Droplets, froid: Snowflake };

const JOURNAL_ICON = {
  connexion: { icon: Wifi, color: 'text-emerald-500' },
  deconnexion: { icon: WifiOff, color: 'text-red-500' },
  activation_survoltage: { icon: Zap, color: 'text-blue-500' },
  modification: { icon: Pencil, color: 'text-neutral-500' },
};

function fmt(iso) {
  if (!iso) return '-';
  try { return new Date(iso).toLocaleString('fr-FR', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }); }
  catch { return '-'; }
}

// Meme sentinelle que sur la liste des clients : le menu deroulant ne sait pas porter
// une valeur vide, alors qu un client non attribue n a justement PAS d agregateur.
// Ouvrir la fiche d un client non attribue et enregistrer sans y toucher lui posait
// « Survoltage » — une attribution creee par le seul fait d avoir ouvert le formulaire.
const NON_ATTRIBUE = 'Non attribué';

export default function ClientDetailPage() {
  // Les regions de reference : la modification est une porte de saisie comme la
  // creation, et le serveur y refuse desormais une valeur hors liste.
  const regions = useRegions();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const { possede } = useCapacites();
  const [client, setClient] = useState(null);
  // L'identifiant du mandat dont le lien part en ce moment, ou null. Un booleen global
  // eteindrait les boutons de TOUS les mandats de la fiche pendant un seul envoi.
  const [envoiLien, setEnvoiLien] = useState(null);
  const [loading, setLoading] = useState(true);
  const [assetDialog, setAssetDialog] = useState(false);
  const ajoutActif = useAjoutActif({ api, toast });
  const [editDialog, setEditDialog] = useState(false);
  const [editClientDialog, setEditClientDialog] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editingAsset, setEditingAsset] = useState(null);
  const [expandedJournal, setExpandedJournal] = useState(null);
  const [journalData, setJournalData] = useState({});
  const [revenusSymphonics, setRevenusSymphonics] = useState({});
  const [newAsset, setNewAsset] = useState({ type: 'PAC', marque: '', modele: '', puissance_kw: '', identifiant_equipement: '' });
  const typesActifs = useTypesActifs();

  /**
   * Envoie au client le lien qui lui permettra de signer ce mandat.
   *
   * RIEN N'EST AUTOMATIQUE, et c'est l'arbitrage de Boubacar du 07/09 : « un bouton,
   * mais bien visible ». Le professionnel garde la main sur le moment -- un dossier
   * peut n'etre pas pret -- et l'ecran se contente de pousser clairement tant qu'aucun
   * lien n'est parti.
   *
   * On RECHARGE la fiche apres coup plutot que de deviner le nouvel etat : l'emission
   * change quatre champs du mandat, dont `date_signature` remise a vide. Les recopier a
   * la main ici, ce serait se donner une seconde verite qui divergera de celle du
   * serveur.
   */
  const envoyerLienSignature = async (mandatId) => {
    setEnvoiLien(mandatId);
    try {
      const { data } = await api.post(`/mandats/${mandatId}/lien-signature`, {});
      toast.success(data?.message || 'Lien de signature envoye au client.');
      await fetchClient();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail
                  : "Le lien de signature n'a pas pu etre envoye.");
    }
    setEnvoiLien(null);
  };

  const fetchClient = useCallback(async () => {
    try { const r = await api.get(`/clients/${id}`); setClient(r.data); }
    catch (e) { console.error(e); }
    setLoading(false);
  }, [id]);
  useEffect(() => { fetchClient(); }, [fetchClient]);

  /**
   * Ce que chaque équipement du client rapporte chez Symphonics.
   *
   * Un seul appel, sur l'analyse du parc, et non un appel par équipement sur
   * `/symphonics/assets/{id}`. Ce dernier calcule le revenu sans passer par les
   * exclusions contractuelles : il afficherait un montant sur un équipement sans mandat
   * signé ou déconnecté, que le total du parc, lui, écarte. La fiche client aurait alors
   * porté un cinquième chiffre en désaccord avec les quatre autres, exactement ce que
   * l'alignement d'un seul barème vient de fermer.
   *
   * L'appel n'est tenté que pour un rôle qui a RÉELLEMENT `tarifs:read`. La liste de
   * rôles écrite ici était une copie de la matrice du serveur : le 30/08 le
   * gestionnaire a perdu cette capacité et la copie ne l'a pas su, si bien que la
   * fiche client partait chercher un revenu qui lui serait refusé — un 403 de plus
   * dans le journal, et une console d'erreurs pour rien.
   */
  useEffect(() => {
    if (!client || client.aggregateur !== 'Symphonics') return;
    if (!possede(CAP.TARIFS_READ)) return;
    let annule = false;
    api.get('/symphonics/assets')
      .then(r => {
        if (annule) return;
        const lignes = [...(r.data?.retenus || []), ...(r.data?.ecartes || [])]
          .filter(l => l.client_id === id);
        setRevenusSymphonics(Object.fromEntries(lignes.map(l => [l.asset_id, l])));
      })
      .catch(e => console.error(e));
    return () => { annule = true; };
  }, [client, possede, id]);

  const fetchJournal = async (assetId) => {
    if (journalData[assetId]) return;
    try {
      const r = await api.get(`/assets/${assetId}/journal`);
      setJournalData(prev => ({ ...prev, [assetId]: r.data }));
    } catch (e) { console.error(e); }
  };

  const toggleJournal = (assetId) => {
    if (expandedJournal === assetId) { setExpandedJournal(null); return; }
    setExpandedJournal(assetId);
    fetchJournal(assetId);
  };

  const handleAddAsset = async (e) => {
    e.preventDefault();
    // Le verdict de l'appareil, ou le refus du serveur, s'affiche : voir `ajoutActif`.
    // Un seul envoi à la fois : voir `useAjoutActif`.
    // Échec : la fenêtre reste ouverte, mais la fiche se recharge (un actif a pu être créé
    // avant une coupure réseau).
    if (!await ajoutActif.ajouter(id, newAsset)) { fetchClient(); return; }
    setAssetDialog(false);
    setNewAsset({ type: 'PAC', marque: '', modele: '', puissance_kw: '', identifiant_equipement: '' });
    fetchClient();
  };

  const handleEditAsset = async (e) => {
    e.preventDefault();
    if (!editingAsset) return;
    try {
      // Le type, la marque et la puissance partent aussi depuis le 30/08. Ils étaient
      // en lecture seule ici alors que l'écran Monitoring les ouvrait déjà : le même
      // appareil se corrigeait ou non selon la porte d'entrée.
      const { data } = await api.put(`/assets/${editingAsset.id}`, {
        type: editingAsset.type,
        marque: editingAsset.marque,
        modele: editingAsset.modele,
        puissance_kw: parseFloat(editingAsset.puissance_kw) || 0,
        identifiant_equipement: editingAsset.identifiant_equipement,
      });
      // Le verdict de la vérification d'enrôlement s'affiche : voir MonitoringPage,
      // même raison. Corriger un identifiant sert à retrouver une machine, et il faut
      // dire si elle a répondu.
      annoncerVerdict(toast, 'Actif mis à jour', data?.enrolement);
      setEditDialog(false); setEditingAsset(null);
      setJournalData(prev => { const n = {...prev}; delete n[editingAsset.id]; return n; });
      fetchClient();
    } catch (err) {
      // Le refus 409 « déjà rattaché à un autre appareil » partait en console : la
      // fenêtre restait ouverte, sans un mot, et l'utilisateur croyait à un bug.
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : "Erreur lors de la mise à jour de l'actif",
                  { duration: 12000 });
    }
  };

  const handleDeleteAsset = async (assetId) => {
    if (!window.confirm('Supprimer cet actif ?')) return;
    try { await api.delete(`/assets/${assetId}`); fetchClient(); } catch (e) { console.error(e); }
  };

  const openEditClient = () => {
    setEditForm({
      civilite: client.civilite || 'M.', prenom: client.prenom || '', nom: client.nom || '',
      email: client.email || '', telephone: client.telephone || '',
      adresse: client.adresse || '', code_postal: client.code_postal || '', ville: client.ville || '',
      region: client.region || '', departement: client.departement || '',
      pdl_prm: client.pdl_prm || '', statut: client.statut || 'prospect',
      aggregateur: client.aggregateur || NON_ATTRIBUE,
    });
    setEditClientDialog(true);
  };

  const handleEditClient = async (e) => {
    e.preventDefault();
    try {
      // Le libelle sentinelle redevient une absence d attribution avant de partir au
      // serveur : sans cette traduction, enregistrer la fiche d un client non attribue
      // creerait un agregateur nomme « Non attribue ».
      const corps = { ...editForm };
      if (corps.aggregateur === NON_ATTRIBUE) corps.aggregateur = '';
      await api.put(`/clients/${id}`, corps);
      setEditClientDialog(false);
      toast.success('Client mis à jour');
      fetchClient();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleDeleteClient = async () => {
    if (!window.confirm(`Supprimer definitivement ${client.prenom} ${client.nom} et tous ses actifs/mandats ?`)) return;
    try {
      await api.delete(`/clients/${id}`);
      toast.success('Client supprimé');
      navigate('/clients');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleDownloadPdf = async (mandatId) => {
    try {
      const r = await api.get(`/mandats/${mandatId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = `mandat_${mandatId}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { console.error(e); }
  };

  const handleDownloadAttestation = async (mandatId) => {
    try {
      const r = await api.get(`/mandats/${mandatId}/attestation-pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = `attestation_${mandatId}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { console.error(e); }
  };

  if (loading) return <div className="flex items-center justify-center h-full"><div className="w-6 h-6 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin" /></div>;
  if (!client) return <div className="p-8 text-center text-neutral-400">Client introuvable<br/><Button variant="outline" onClick={() => navigate('/clients')} className="mt-4">Retour</Button></div>;

  const assets = client.assets || [];
  const mandats = client.mandats || [];
  const historique = client.historique || [];
  // OU EST PARTI CE MANDAT. La trace est posée sur le mandat au moment de l'envoi et
  // ne bouge plus, même si le client change d'agrégateur ensuite. C'est la différence
  // entre « à qui ce client appartient aujourd'hui » et « où ce papier est parti ».
  // OU CE MANDAT EN EST DE SA TRANSMISSION A L'AGREGATEUR — Symphonics ou Survoltage.
  //
  // ⚠️ LES LIBELLES DISAIENT « Envoyé » ET « Non envoyé » TOUT COURT, ET C'EST DEVENU
  // AMBIGU LE 07/09. Depuis le canal de signature à distance, un mandat porte DEUX
  // envois qui n'ont rien à voir : le lien de signature qui part au client, et la pièce
  // qui part chez l'agrégateur. Relevé de Boubacar en recette le 08/09 : il a lu
  // « Non envoyé » sur un mandat dont le lien de signature était bien parti, et reçu.
  //
  // Chaque libellé nomme donc désormais SA destination. Un badge qui oblige à deviner
  // de quel envoi il parle est un badge qui se lit de travers une fois sur deux.
  const ENVOI = {
    envoye: { label: 'Transmis à l\u2019agrégateur', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    dry_run: { label: 'Répétition, non transmis', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
    echec: { label: 'Échec de transmission', cls: 'bg-red-50 text-red-700 border-red-200' },
    non_envoye: { label: 'Non transmis à l\u2019agrégateur', cls: 'bg-neutral-100 text-neutral-600 border-neutral-200' },
  };
  const envoiDe = (m) => ENVOI[m.envoi_resultat || 'non_envoye'] || ENVOI.non_envoye;

  return (
    <div className="p-8 md:p-10 max-w-5xl space-y-6" data-testid="client-detail-page">
      {/* Breadcrumb */}
      <Breadcrumb items={[
        { label: 'Clients', to: '/clients' },
        { label: `${client.prenom} ${client.nom}` },
      ]} />

      {/* Header */}
      <div className="flex items-center gap-4 flex-wrap">
        <Button variant="ghost" size="sm" onClick={() => navigate('/clients')} data-testid="back-to-clients-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Retour
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-semibold tracking-tight text-neutral-900">{client.civilite} {client.prenom} {client.nom}</h1>
          </div>
          <p className="text-sm text-neutral-400">{client.ville} {client.departement && `(${client.departement})`}</p>
        </div>
        {client.statut !== 'actif' && client.statut !== 'resilie' && (
          <Button onClick={() => navigate(`/clients/${id}/mandat`)} data-testid="create-mandat-btn"><FileText className="w-4 h-4 mr-1" /> Signer un mandat</Button>
        )}
        <Button variant="outline" size="sm" onClick={openEditClient} data-testid="edit-client-btn">
          <Pencil className="w-4 h-4 mr-1" /> Modifier
        </Button>
        {currentUser?.role === 'admin' && (
          <Button variant="outline" size="sm" className="text-red-500 hover:text-red-600 hover:bg-red-50" onClick={handleDeleteClient} data-testid="delete-client-btn">
            <Trash2 className="w-4 h-4 mr-1" /> Supprimer
          </Button>
        )}
      </div>

      {/* Bandeau de statut + bloc resume */}
      {(() => {
        const banner = STATUS_BANNER[client.statut] || STATUS_BANNER.prospect;
        const connectedAssets = assets.filter(a => a.statut_connexion === 'connecte').length;
        return (
          <div className={`rounded-xl ${banner.bg} ring-1 ${banner.ring} p-5`} data-testid="client-status-banner">
            <div className="flex flex-wrap items-center gap-4 mb-4">
              <div className="flex items-center gap-2">
                <span className={`w-2.5 h-2.5 rounded-full ${banner.dot} animate-pulse`} />
                <span className={`text-[13px] font-semibold uppercase tracking-wider ${banner.text}`} data-testid="client-status-label">
                  {banner.label}
                </span>
              </div>
              <span className="text-[11px] text-neutral-500">
                Créé le {fmt(client.created_at)}{client.created_by_name ? ` par ${client.created_by_name}` : ''}
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <SummaryTile
                label="Actifs"
                value={`${assets.length}`}
                sub={`${connectedAssets} connecté${connectedAssets > 1 ? 's' : ''}`}
                tid="summary-actifs"
              />
              <SummaryTile
                label="Statut mandat"
                value={client.mandat_status_summary === 'actif' ? 'Actif' : client.mandat_status_summary === 'signe' ? 'Signé' : 'Aucun'}
                sub={`${mandats.length} mandat${mandats.length > 1 ? 's' : ''}`}
                tid="summary-mandat"
              />
              <SummaryTile
                label="Agrégateur"
                value={client.aggregateur || 'Non assigné'}
                sub={client.aggregateur ? 'Assigné après installation' : 'À assigner après installation'}
                tid="summary-aggregateur"
              />
              <SummaryTile
                label="Dernière activité"
                value={fmt(client.derniere_activite)}
                sub={client.derniere_action || '—'}
                tid="summary-activite"
              />
            </div>
          </div>
        );
      })()}

      <Tabs defaultValue="info" className="space-y-4">
        <TabsList>
          <TabsTrigger value="info" data-testid="tab-info">Informations</TabsTrigger>
          <TabsTrigger value="assets" data-testid="tab-assets">Actifs ({assets.length})</TabsTrigger>
          <TabsTrigger value="mandats" data-testid="tab-mandats">Mandats ({mandats.length})</TabsTrigger>
          <TabsTrigger value="historique" data-testid="tab-historique">Historique</TabsTrigger>
        </TabsList>

        {/* Info */}
        <TabsContent value="info">
          <div className="rounded-lg border border-neutral-200/80 bg-white p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-3">
                <h3 className="text-[11px] uppercase tracking-wider text-neutral-400 font-medium">Coordonnees</h3>
                <Row l="Civilite" v={client.civilite} />
                <Row l="Prenom" v={client.prenom} />
                <Row l="Nom" v={client.nom} />
                <Row l="Email" v={client.email} />
                <Row l="Telephone" v={client.telephone} />
              </div>
              <div className="space-y-3">
                <h3 className="text-[11px] uppercase tracking-wider text-neutral-400 font-medium">Adresse & Reseau</h3>
                <Row l="Adresse" v={client.adresse} /><Row l="Code postal / Ville" v={`${client.code_postal} ${client.ville}`} />
                <Row l="Region" v={client.region} /><Row l="PDL/PRM" v={client.pdl_prm} mono />
              </div>
              <div className="space-y-3">
                <h3 className="text-[11px] uppercase tracking-wider text-neutral-400 font-medium">Mandat</h3>
                <div className="flex items-baseline justify-between py-1.5">
                  <span className="text-[13px] text-neutral-400">Agrégateur</span>
                  <Badge variant="outline" className={client.aggregateur === 'Survoltage' ? 'bg-violet-50 text-violet-700 border-violet-200' : client.aggregateur === 'Symphonics' ? 'bg-cyan-50 text-cyan-700 border-cyan-200' : 'bg-neutral-50 text-neutral-600'}>
                    {client.aggregateur || 'Non attribué'}
                  </Badge>
                </div>
                <div className="flex items-baseline justify-between py-1.5">
                  <span className="text-[13px] text-neutral-400">Statut mandat</span>
                  <span className={`status-badge ${
                    client.mandat_status_summary === 'actif' ? 'bg-emerald-50 text-emerald-700' :
                    client.mandat_status_summary === 'signe' ? 'bg-blue-50 text-blue-700' :
                    'bg-neutral-100 text-neutral-500'
                  }`}>
                    {client.mandat_status_summary === 'actif' ? 'Actif' : client.mandat_status_summary === 'signe' ? 'Signé' : 'Aucun mandat'}
                  </span>
                </div>
                {/* Le mandat signe se declare chez Enedis a la signature, tous canaux
                    confondus : le statut dit si c'est fait, et pourquoi sinon. */}
                <DeclarationEnedis clientId={client.id} />
                {/* L'envoi n'est plus une manipulation d'ecran : il suit l'assignation
                    a un agregateur, decidee apres installation. Ne restent ici que les
                    deux informations utiles quand quelque chose s'est mal passe. */}
                {client.symphonics_last_error && (
                  <p className="text-[12px] text-red-600 text-right">{client.symphonics_last_error}</p>
                )}
                {client.symphonics_last_sync_at && (
                  <p className="text-[11px] text-neutral-400 text-right">Dernier envoi: {fmt(client.symphonics_last_sync_at)}</p>
                )}
              </div>
              <div className="space-y-3">
                <h3 className="text-[11px] uppercase tracking-wider text-neutral-400 font-medium">Fiche</h3>
                <Row l="Date de creation" v={fmt(client.created_at)} />
                <Row l="Installateur" v={client.created_by_name || '-'} />
                <Row l="Role" v={client.created_by_role || '-'} />
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Assets */}
        <TabsContent value="assets">
          <div className="space-y-3">
            {/* Le raccordement aux agregateurs, AU-DESSUS de la liste des
                actifs : declarer un equipement est desormais le geste qui
                ouvre le revenu, et il se decide en regardant les actifs. */}
            <RaccordementAgregateurs clientId={id} />

            {/* L envoi du dossier, juste sous les declarations : declarer ne fait rien
                partir, c est ce bloc qui transmet (06/10/2026). Admin seul, comme le
                reste de l administration du bridge. */}
            {possede(CAP.INTEGRATIONS_MANAGE) && <EnvoisAgregateurs clientId={id} />}

            <div className="flex justify-end">
              {/* Pendant l'envoi, la fenêtre ne se ferme pas : la saisie disparaîtrait alors
                  que l'actif est peut-être déjà créé. */}
              <Dialog open={assetDialog} onOpenChange={o => { if (!ajoutActif.enCours) setAssetDialog(o); }}>
                <DialogTrigger asChild><Button size="sm" data-testid="add-asset-btn"><Plus className="w-4 h-4 mr-1" /> Ajouter</Button></DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Ajouter un actif</DialogTitle></DialogHeader>
                  <form onSubmit={handleAddAsset} className="space-y-3">
                    <div><Label className="text-[13px]">Type</Label>
                      <Select value={newAsset.type} onValueChange={v => setNewAsset(a => ({...a, type: v}))}>
                        <SelectTrigger data-testid="new-asset-type"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {/* Les types viennent de Parametres > Menus, jamais d'une liste
                              ecrite ici. David a ajoute « PAC (sans ECS) » et ne l'a
                              jamais retrouve dans cette liste deroulante : elle portait
                              ses cinq valeurs en dur. */}
                          {typesActifs.map(type => (
                            <SelectItem key={type} value={type}>{TYPE_LABEL[type] || type}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label className="text-[13px]">Marque *</Label><Input required value={newAsset.marque} onChange={e => setNewAsset(a => ({...a, marque: e.target.value}))} data-testid="new-asset-marque" /></div>
                      <div><Label className="text-[13px]">Modele *</Label><Input required value={newAsset.modele} onChange={e => setNewAsset(a => ({...a, modele: e.target.value}))} data-testid="new-asset-modele" /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label className="text-[13px]">Puissance (kW) *</Label><Input required type="number" step="0.1" value={newAsset.puissance_kw} onChange={e => setNewAsset(a => ({...a, puissance_kw: e.target.value}))} data-testid="new-asset-puissance" /></div>
                      <div><Label className="text-[13px]">ID equipement *</Label><Input required value={newAsset.identifiant_equipement} onChange={e => setNewAsset(a => ({...a, identifiant_equipement: e.target.value}))} data-testid="new-asset-id" /></div>
                    </div>
                    <Button type="submit" className="w-full" disabled={ajoutActif.enCours} data-testid="submit-new-asset-btn">
                      {ajoutActif.enCours ? "Vérification de l'appareil…" : 'Ajouter'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <Dialog open={editDialog} onOpenChange={setEditDialog}>
              <DialogContent>
                <DialogHeader><DialogTitle>Modifier l'actif</DialogTitle></DialogHeader>
                {editingAsset && (
                  <form onSubmit={handleEditAsset} className="space-y-3">
                    <div><Label className="text-[13px]">Type</Label>
                      <Select value={editingAsset.type} onValueChange={v => setEditingAsset(a => ({...a, type: v}))}>
                        <SelectTrigger data-testid="edit-asset-type"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {typesActifs.map(type => (
                            <SelectItem key={type} value={type}>{TYPE_LABEL[type] || type}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label className="text-[13px]">Marque *</Label><Input required value={editingAsset.marque || ''} onChange={e => setEditingAsset(a => ({...a, marque: e.target.value}))} data-testid="edit-asset-marque" /></div>
                      <div><Label className="text-[13px]">Modele *</Label><Input required value={editingAsset.modele || ''} onChange={e => setEditingAsset(a => ({...a, modele: e.target.value}))} data-testid="edit-asset-modele" /></div>
                    </div>
                    <div><Label className="text-[13px]">Puissance (kW)</Label><Input type="number" step="0.1" value={editingAsset.puissance_kw ?? ''} onChange={e => setEditingAsset(a => ({...a, puissance_kw: e.target.value}))} data-testid="edit-asset-puissance" /></div>
                    <div><Label className="text-[13px]">{LIBELLE_IDENTIFIANT[editingAsset.type] || LIBELLE_IDENTIFIANT.defaut} *</Label><Input required value={editingAsset.identifiant_equipement || ''} onChange={e => setEditingAsset(a => ({...a, identifiant_equipement: e.target.value}))} data-testid="edit-asset-id" />
                      {/* Ce champ n'est pas de la description : il désigne le matériel
                          réel. Le serveur rejoue la vérification d'enrôlement et refuse
                          un numéro déjà rattaché ailleurs — le dire ICI évite de
                          l'apprendre par un refus. */}
                      <p className="text-[11px] text-neutral-500 mt-1">Changer ce numéro désigne un autre appareil : la vérification est rejouée, et un identifiant déjà rattaché à un autre dossier est refusé.</p></div>
                    <Button type="submit" className="w-full" data-testid="submit-edit-asset-btn">Enregistrer</Button>
                  </form>
                )}
              </DialogContent>
            </Dialog>

            {assets.length === 0 ? (
              <div className="p-8 text-center text-neutral-400 border border-dashed border-neutral-200 rounded-lg">Aucun actif enregistré</div>
            ) : assets.map(asset => {
              const Icon = TYPE_ICON[asset.type] || HelpCircle;
              const ConnIcon = CONN_ICON[asset.statut_connexion] || HelpCircle;
              const m = asset.monitoring || {};
              const isExpanded = expandedJournal === asset.id;
              const journal = journalData[asset.id] || [];
              return (
                <div key={asset.id} className="rounded-lg border border-neutral-200/80 bg-white overflow-hidden" data-testid={`asset-card-${asset.id}`}>
                  <div className="p-4">
                    {/* Asset Header */}
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`w-8 h-8 rounded-lg bg-neutral-50 flex items-center justify-center ${TYPE_COLOR[asset.type] || 'text-neutral-400'}`}>
                        <Icon className="w-4.5 h-4.5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-neutral-900">{TYPE_LABEL[asset.type] || asset.type}</span>
                          <span className={`status-badge ${MANDAT_CFG[asset.statut_mandat]?.cls || ''}`}>{MANDAT_CFG[asset.statut_mandat]?.label || asset.statut_mandat}</span>
                        </div>
                        <p className="text-[12px] text-neutral-500">{asset.marque} {asset.modele} &mdash; {asset.puissance_kw} kW</p>
                        <p className="text-[11px] text-neutral-400 font-mono">{asset.identifiant_equipement}</p>
                      </div>
                      <div className={`flex items-center gap-1 text-[11px] font-medium ${CONN_COLOR[asset.statut_connexion] || 'text-neutral-400'}`}>
                        <ConnIcon className="w-3.5 h-3.5" />
                        <span className="capitalize hidden sm:inline">{asset.statut_connexion}</span>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => { setEditingAsset({...asset}); setEditDialog(true); }} data-testid={`edit-asset-${asset.id}`}><Pencil className="w-3.5 h-3.5 text-neutral-400" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteAsset(asset.id)} data-testid={`delete-asset-${asset.id}`}><Trash2 className="w-3.5 h-3.5 text-neutral-400" /></Button>
                    </div>

                    {/* Monitoring Data - inline */}
                    {asset.statut_connexion !== 'inconnu' && (
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-3 rounded-md bg-neutral-50/70 text-[12px]">
                        {asset.type === 'PAC' && (<>
                          <Metric icon={m.etat === 'ON' ? Activity : CircleDot} label="Etat" value={m.etat || '-'} accent={m.etat === 'ON' ? 'text-emerald-600' : 'text-neutral-400'} />
                          <Metric icon={MODE_ICON[m.mode] || Flame} label="Mode" value={m.mode || '-'} />
                          {/* Mêmes noms que `AssetPilotageRow` et `AssetMonitoringCard` : la
                              veille Tuya écrit `temperature_mesuree`, jamais `temperature_c`
                              (champ batterie), et une consommation non mesurée s'écrit
                              « inconnu », pas « 0 kW ». Retour de David du 24/09. */}
                          <Metric icon={Zap} label="Consommation" value={mesureTexte(m.consommation_kw ?? m.puissance_consommee_kw, 'kW')} />
                          <Metric icon={ThermometerSun} label="Temperature" value={mesureTexte(m.temperature_mesuree ?? m.temperature_c, '°C')} />
                          {m.en_degivrage && <span className="col-span-full text-[11px] text-blue-500 flex items-center gap-1"><Snowflake className="w-3 h-3" /> En degivrage</span>}
                        </>)}
                        {/* « Rendement » a disparu, et ce n'est pas un oubli : AUCUN point
                            d'entree de l'API S-Miles ne rend un rendement. Il valait donc
                            `0%` en permanence. Le calculer en divisant la puissance par la
                            puissance installee donnerait un TAUX DE CHARGE, qui n'est pas
                            un rendement — un chiffre faux sous un nom credible. A sa place,
                            l'etat de l'onduleur, que l'API rend vraiment. */}
                        {asset.type === 'PV' && (<>
                          <Metric icon={Zap} label="Production" value={productionCouranteTexte(m)} accent="text-amber-600" />
                          {/* LA PUISSANCE INSTALLEE VIENT DE L ACTIF, PAS DES MESURES, et c est la valeur
                          que HOYMILES declare — jamais `puissance_kw`, saisie dans Flexcore. Ecart
                          mesure sur la centrale du showroom le 14/09 : 0,5 kWc chez Hoymiles contre
                          1,0 kW dans la fiche. Afficher la seconde donnerait une puissance fausse du
                          DOUBLE, sous un libelle credible.

                          ⚠️ CE CHAMP LISAIT `m.puissance_crete_kwc` JUSQU AU 14/09, et affichait donc
                          un tiret perpetuel : aucun emetteur du backend n a jamais ecrit ce nom. Un
                          couple lecteur/emetteur avait diverge en silence. Meme arbitrage et meme
                          source que `CentralePvPanel`, qui porte le raisonnement complet. */}
                          <Metric icon={Sun} label="Crete" value={mesureTexte(asset.capacite_kwc, 'kWc')} />
                          <Metric icon={Activity} label="Aujourd'hui" value={mesureTexte(m.energie_jour_kwh, 'kWh')} />
                          <Metric icon={CircleDot} label="Onduleur" value={etatOnduleurTexte(m)} />
                          {!m.puissance_courante_prouvee && dernierPointPvTexte(m) && (
                            <span className="col-span-full text-[11px] text-neutral-400">
                              Dernier point connu : {dernierPointPvTexte(m)}
                            </span>
                          )}
                          {(m.alarmes || []).map((alarme) => (
                            <span key={alarme} className="col-span-full text-[11px] text-amber-600">{alarme}</span>
                          ))}
                        </>)}
                        {(asset.type === 'Ballon' || asset.type === 'ECS') && (<>
                          <Metric icon={m.etat === 'ON' ? Activity : CircleDot} label="Etat" value={m.etat || '-'} accent={m.etat === 'ON' ? 'text-emerald-600' : 'text-neutral-400'} />
                          <Metric icon={ThermometerSun} label="Eau" value={`${m.temperature_eau_c || '-'}C`} />
                          <Metric icon={ThermometerSun} label="Consigne" value={`${m.temperature_consigne_c || '-'}C`} />
                        </>)}
                        {asset.type === 'Batterie' && (<>
                          <Metric icon={Activity} label="Etat" value={m.etat || '-'} accent={m.etat === 'charge' ? 'text-emerald-600' : m.etat === 'decharge' ? 'text-orange-600' : 'text-neutral-500'} />
                          <Metric icon={BatteryCharging} label="SOC" value={`${m.soc_pct ?? '-'}%`} accent="text-violet-600" />
                          <Metric icon={Zap} label="Puissance" value={`${m.puissance_kw ?? '-'} kW`} />
                          <Metric icon={Zap} label="Tension" value={`${m.tension_v ?? '-'} V`} />
                          <Metric icon={ThermometerSun} label="Temp." value={`${m.temperature_c ?? '-'}°C`} />
                          <Metric icon={Activity} label="Santé" value={`${m.sante_pct ?? '-'}%`} accent="text-emerald-600" />
                          <Metric icon={m.mqtt_status === 'connecte' ? Wifi : WifiOff} label="MQTT" value={m.mqtt_status === 'connecte' ? 'Connecté' : 'Déconnecté'} accent={m.mqtt_status === 'connecte' ? 'text-emerald-600' : 'text-red-500'} />
                        </>)}
                      </div>
                    )}

                    {/* Ce que cet équipement rapporte, ou pourquoi il ne rapporte rien.
                        Le motif vient du serveur : « aucun mandat signé » et « type non
                        tarifé » n'appellent pas la même action de l'exploitant. */}
                    {revenusSymphonics[asset.id] && (
                      <RevenuSymphonicsActif
                        ligne={revenusSymphonics[asset.id]}
                        testId={`symphonics-revenu-${asset.id}`}
                      />
                    )}

                    {/* Meta */}
                    <div className="flex items-center gap-4 mt-3 text-[11px] text-neutral-400 flex-wrap">
                      <span className="font-mono">{asset.identifiant_equipement}</span>
                      {asset.dernier_contact && (
                        <span className="flex items-center gap-1" data-testid={`asset-last-contact-${asset.id}`}>
                          <Clock className="w-3 h-3" /> Dernière connexion : {fmt(asset.dernier_contact)}
                        </span>
                      )}
                      {/* « Fin engagement » retiree le 28/08 (item B7) : la date etait
                          posee a la signature, a +3 ans, sur la foi d'une moyenne mal
                          interpretee. Il n'existe pas de fin d'engagement. */}
                      <button className="ml-auto flex items-center gap-1 hover:text-neutral-600 transition-colors" onClick={() => toggleJournal(asset.id)} data-testid={`toggle-journal-${asset.id}`}>
                        Journal {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                      </button>
                    </div>
                  </div>

                  {/* Journal */}
                  {isExpanded && (
                    <div className="border-t border-neutral-100 bg-neutral-50/50 px-4 py-3 max-h-[250px] overflow-y-auto" data-testid={`journal-${asset.id}`}>
                      {journal.length === 0 ? (
                        <p className="text-[11px] text-neutral-400 text-center py-2">Aucune entree</p>
                      ) : journal.map((entry, i) => {
                        const jCfg = JOURNAL_ICON[entry.type] || { icon: CircleDot, color: 'text-neutral-400' };
                        const JIcon = jCfg.icon;
                        return (
                          <div key={i} className="flex items-start gap-2.5 py-1.5 border-b border-neutral-100/50 last:border-0">
                            <JIcon className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${jCfg.color}`} />
                            <div className="flex-1 min-w-0">
                              <p className="text-[12px] text-neutral-700">{entry.details}</p>
                              <p className="text-[10px] text-neutral-400">{fmt(entry.date)} &mdash; {entry.user}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </TabsContent>

        {/* Mandats */}
        <TabsContent value="mandats">
          {mandats.length === 0 ? (
            <div className="p-8 text-center text-neutral-400 border border-dashed border-neutral-200 rounded-lg">Aucun mandat</div>
          ) : (
            <div className="space-y-2">
              {mandats.map(m => (
                <div key={m.id} className="flex items-center gap-4 p-4 rounded-lg border border-neutral-200/80 bg-white" data-testid={`mandat-card-${m.id}`}>
                  <FileText className="w-5 h-5 text-neutral-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-neutral-900">Mandat de flexibilite</span>
                      <span className={`status-badge ${MANDAT_CFG[m.statut]?.cls || ''}`}>{MANDAT_CFG[m.statut]?.label || m.statut}</span>
                    </div>
                    {/* ⚠️ LA DATE NE S'AFFICHE QUE SI LE MANDAT EST SIGNÉ. Depuis le
                        canal à distance, un mandat qui attend son client n'a plus de
                        `date_signature` — retirée exprès pour que la pièce ne se date
                        pas avant le trait — et cette ligne affichait « Signé le - par
                        Marc Dupont ». Un dossier en attente se lisait comme un dossier
                        fini, à une date inconnue. */}
                    {m.etat_signature?.code === 'signe' || (!m.etat_signature && m.date_signature) ? (
                      <p className="text-[12px] text-neutral-500">Signé le {fmt(m.date_signature)} par {m.signe_par_nom || '-'}</p>
                    ) : (
                      <div className="mt-1"><EtatSignatureMandat etat={m.etat_signature} /></div>
                    )}
                    <p className="text-[11px] text-neutral-400">{m.asset_ids?.length || 0} actif(s) &mdash; 4/4 consentements</p>
                    <p className="text-[11px] mt-1 flex items-center gap-1.5 flex-wrap" data-testid={`mandat-envoi-${m.id}`}>
                      <span className={`status-badge ${envoiDe(m).cls}`}>{envoiDe(m).label}</span>
                      {m.envoye_a && (
                        <span className="text-neutral-500">
                          chez <strong className="text-neutral-700">{m.envoye_a}</strong>
                          {m.envoye_le && <> le {fmt(m.envoye_le)}</>}
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    {/* LE GESTE EST À CÔTÉ DE L'ÉTAT QUI L'APPELLE, et il change de
                        poids avec lui.

                        Boubacar, le 07/09 : « un bouton, mais bien visible ». Le lien
                        ne part JAMAIS tout seul — le professionnel garde la main et
                        choisit quand le dossier est prêt — mais l'écran pousse
                        clairement à le faire tant qu'aucun lien n'est parti : bouton
                        plein sur un dossier à envoyer, discret pour un simple renvoi.
                        Un dossier signé n'en propose aucun : il n'y a plus rien à
                        envoyer. */}
                    {m.etat_signature && m.etat_signature.code !== 'signe' && (
                      <Button
                        size="sm"
                        variant={m.etat_signature.code === 'a_envoyer' ? 'default' : 'outline'}
                        disabled={envoiLien === m.id}
                        onClick={() => envoyerLienSignature(m.id)}
                        data-testid={`lien-signature-${m.id}`}
                      >
                        <Send className="w-3.5 h-3.5 mr-1" />
                        {envoiLien === m.id ? 'Envoi...'
                          : m.etat_signature.code === 'a_envoyer' ? 'Faire signer'
                          : 'Renvoyer le lien'}
                      </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={() => handleDownloadPdf(m.id)} data-testid={`download-pdf-${m.id}`}>
                      <Download className="w-3.5 h-3.5 mr-1" /> Mandat
                    </Button>
                    {/* Le bouton n'apparaît QUE si une réception a été signée.
                        Mesure du 31/08 : un mandat créé depuis le CRM n'a aucune
                        réponse d'attestation, et le PDF sortait avec ses quatre
                        questions vides — ce qui se lit comme un client qui n'a rien
                        voulu répondre, alors que personne ne lui a jamais posé la
                        question. Les mandats issus du parcours CFTSign, eux, portent
                        bien leurs quatre réponses. */}
                    {Object.values(m.satisfaction || {}).some(r => r?.answer) ? (
                      <Button variant="outline" size="sm" onClick={() => handleDownloadAttestation(m.id)} data-testid={`download-attestation-${m.id}`}>
                        <Download className="w-3.5 h-3.5 mr-1" /> Attestation
                      </Button>
                    ) : (
                      <span className="text-[11px] text-neutral-400 self-center"
                            title="Ce mandat a été signé sans passer par une réception de chantier."
                            data-testid={`sans-attestation-${m.id}`}>
                        Sans attestation
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Historique */}
        <TabsContent value="historique">
          <div className="rounded-lg border border-neutral-200/80 bg-white p-4">
            {historique.length === 0 ? <p className="text-center text-neutral-400 py-4">Aucun historique</p> : (
              <div className="space-y-1">
                {[...historique].reverse().map((h, i) => (
                  <div key={i} className="flex items-start gap-3 py-2 border-b border-neutral-50 last:border-0">
                    <div className="w-1.5 h-1.5 rounded-full bg-neutral-300 mt-2 flex-shrink-0" />
                    <div>
                      <p className="text-[13px] text-neutral-700">{h.action}</p>
                      <p className="text-[11px] text-neutral-400">{fmt(h.date)} &mdash; {h.user}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Edit Client Dialog */}
      <Dialog open={editClientDialog} onOpenChange={setEditClientDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Modifier le client</DialogTitle></DialogHeader>
          <form onSubmit={handleEditClient} className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Civilite</Label>
                <Select value={editForm.civilite} onValueChange={v => setEditForm(f => ({ ...f, civilite: v }))}>
                  <SelectTrigger className="h-9" data-testid="edit-client-civilite"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="M.">M.</SelectItem><SelectItem value="Mme">Mme</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Prenom</Label><Input className="h-9" value={editForm.prenom || ''} onChange={e => setEditForm(f => ({ ...f, prenom: e.target.value }))} data-testid="edit-client-prenom" /></div>
              <div><Label className="text-xs">Nom</Label><Input className="h-9" value={editForm.nom || ''} onChange={e => setEditForm(f => ({ ...f, nom: e.target.value }))} data-testid="edit-client-nom" /></div>
            </div>
            <div>
              <Label className="text-xs">Adresse</Label>
              {/* LA PORTE QU'ON OUBLIE, ET C'EST LA PLUS PIEGEUSE DES QUATRE.
                  Cet ecran n'a AUCUN enchainement code postal -> region : les trois
                  champs se saisissent a la main. Deplacer un client du Mans a Lyon en
                  ne changeant que l'adresse laissait donc « Pays de la Loire » en
                  place, sans que rien ne le signale -- et c'est cette region-la qui
                  part au mandat.
                  ⚠️ Ici un choix ECRASE region et departement, la ou `majCodePostal`
                  de l'ecran de creation ne remplit que le vide. La difference est
                  voulue : la-bas on protege une saisie en cours, ici l'utilisateur
                  vient de DESIGNER une autre adresse dans la liste. Garder l'ancienne
                  region apres ce geste serait garder une valeur qu'on sait fausse. */}
              <RechercheAdresse
                valeur={editForm.adresse}
                onChange={v => setEditForm(f => ({ ...f, adresse: v }))}
                onChoisir={p => {
                  setEditForm(f => ({ ...f, adresse: p.adresse,
                    code_postal: p.code_postal, ville: p.ville }));
                  if (!ressembleAUnCodePostal(p.code_postal)) return;
                  lireCodePostal(p.code_postal)
                    .then(geo => setEditForm(f => ({ ...f,
                      region: geo.region || f.region,
                      departement: geo.departement || f.departement })))
                    .catch(() => { /* code postal inconnu : les deux champs restent
                                      modifiables a la main, comme avant ce lot. */ });
                }}
                classeChamp="h-9"
                testid="edit-client-adresse" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Code postal</Label><Input className="h-9" value={editForm.code_postal || ''} onChange={e => setEditForm(f => ({ ...f, code_postal: e.target.value }))} data-testid="edit-client-cp" /></div>
              <div><Label className="text-xs">Ville</Label><Input className="h-9" value={editForm.ville || ''} onChange={e => setEditForm(f => ({ ...f, ville: e.target.value }))} data-testid="edit-client-ville" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Region</Label>
                <Select value={editForm.region || ''} onValueChange={v => setEditForm(f => ({ ...f, region: v }))}>
                  <SelectTrigger className="h-9" data-testid="edit-client-region"><SelectValue placeholder="Selectionner une region" /></SelectTrigger>
                  <SelectContent>
                    {regions.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select></div>
              <div><Label className="text-xs">Departement</Label><Input className="h-9" value={editForm.departement || ''} onChange={e => setEditForm(f => ({ ...f, departement: e.target.value }))} /></div>
            </div>
            <div><Label className="text-xs">PDL/PRM</Label><Input className="h-9" value={editForm.pdl_prm || ''} onChange={e => setEditForm(f => ({ ...f, pdl_prm: e.target.value }))} data-testid="edit-client-pdl" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Email</Label><Input className="h-9" type="email" value={editForm.email || ''} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} data-testid="edit-client-email" /></div>
              <div><Label className="text-xs">Telephone</Label><Input className="h-9" value={editForm.telephone || ''} onChange={e => setEditForm(f => ({ ...f, telephone: e.target.value }))} data-testid="edit-client-tel" /></div>
            </div>
            <div>
              <Label className="text-xs">Statut</Label>
              <Select value={editForm.statut} onValueChange={v => setEditForm(f => ({ ...f, statut: v }))}>
                <SelectTrigger className="h-9" data-testid="edit-client-statut"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="prospect">Prospect</SelectItem>
                  <SelectItem value="mandat_envoye">Mandat envoye</SelectItem>
                  <SelectItem value="signe">Signé</SelectItem>
                  <SelectItem value="actif">Actif</SelectItem>
                  <SelectItem value="resilie">Resilie</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Agrégateur</Label>
              <Select value={editForm.aggregateur} onValueChange={v => setEditForm(f => ({ ...f, aggregateur: v }))}>
                <SelectTrigger className="h-9" data-testid="edit-client-aggregateur"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NON_ATTRIBUE}>{NON_ATTRIBUE}</SelectItem>
                  <OptionsAgregateurs />
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" className="w-full" data-testid="submit-edit-client-btn">
              <Save className="w-4 h-4 mr-1" /> Enregistrer
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ l, v, mono }) {
  return (
    <div className="flex items-baseline justify-between py-1.5">
      <span className="text-[13px] text-neutral-400">{l}</span>
      <span className={`text-[13px] font-medium text-neutral-900 ${mono ? 'font-mono text-[12px]' : ''}`}>{v || '-'}</span>
    </div>
  );
}

function Metric({ icon: Icon, label, value, accent }) {
  return (
    <div className="flex items-center gap-2">
      <Icon className={`w-3.5 h-3.5 ${accent || 'text-neutral-400'}`} />
      <div>
        <p className="text-[10px] text-neutral-400">{label}</p>
        <p className={`text-[12px] font-medium ${accent || 'text-neutral-700'}`}>{value}</p>
      </div>
    </div>
  );
}

function SummaryTile({ label, value, sub, tid }) {
  return (
    <div className="rounded-lg bg-white/70 backdrop-blur p-3 ring-1 ring-white/60" data-testid={tid}>
      <p className="text-[10px] uppercase tracking-wider text-neutral-500 font-medium">{label}</p>
      <p className="text-base font-semibold text-neutral-900 mt-0.5 leading-tight">{value}</p>
      {sub && <p className="text-[10px] text-neutral-500 mt-0.5 truncate">{sub}</p>}
    </div>
  );
}

const euros = (v) => (v == null
  ? '—'
  : `${Number(v).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`);

/**
 * Estimation Symphonics d'un équipement, tel que le calcul du parc le retient.
 *
 * Une ligne écartée porte son motif et le dit : l'exploitant doit pouvoir distinguer
 * « Symphonics n'a pas encore tarifé ce type » (rien à faire) de « aucun mandat signé »
 * (il y a quelque chose à faire).
 */
function RevenuSymphonicsActif({ ligne, testId }) {
  if (ligne.motif) {
    return (
      <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-100 bg-amber-50/70 p-2.5 text-[11px] text-amber-700" data-testid={testId}>
        <span className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-amber-500" />
        <div className="flex-1">
          <p className="font-medium">Estimation Symphonics : {ligne.motif_libelle || ligne.motif}</p>
          {ligne.parametres_manquants?.length > 0 && (
            <p className="mt-0.5 text-[10px] text-amber-600">
              Paramètres manquants pour ce type : {ligne.parametres_manquants.join(', ')}
            </p>
          )}
        </div>
      </div>
    );
  }
  return (
    <div className="mt-3 rounded-md border border-cyan-100 bg-cyan-50/40 p-3" data-testid={testId}>
      <div className="mb-2 flex items-center gap-1.5">
        <span className="h-1.5 w-1.5 rounded-full bg-cyan-500" />
        <p className="text-[11px] font-medium uppercase tracking-wider text-cyan-800">
          Estimation Symphonics
        </p>
        {ligne.personnalise && (
          <span className="rounded bg-white px-1.5 py-0.5 text-[9px] font-semibold text-cyan-700" data-testid={`${testId}-personnalise`}>
            Réglage propre à cet équipement
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2 text-[11px] sm:grid-cols-4">
        <div>
          <p className="text-[10px] text-neutral-400">MECAPA /an estimé</p>
          <p className="font-semibold text-neutral-900">{euros(ligne.capacitaire_an)}</p>
        </div>
        <div>
          <p className="text-[10px] text-neutral-400">Énergie /an estimée</p>
          <p className="font-semibold text-neutral-900">{euros(ligne.energie_an)}</p>
        </div>
        <div>
          <p className="text-[10px] text-neutral-400">Annuel estimé</p>
          <p className="font-semibold text-cyan-700">{euros(ligne.annuel)}</p>
        </div>
        <div>
          <p className="text-[10px] text-neutral-400">Mensuel estimé</p>
          <p className="font-semibold text-cyan-700">{euros(ligne.mensuel)}</p>
        </div>
      </div>
      <MentionEstimation source="symphonics" bloc="fiche-client" className="mt-2" />
    </div>
  );
}
